import {
  Container,
  EmptySonarStateContainer,
  NoUsersTitle,
  SonarEmptyStateSubtitle,
  SonarEmptyStateTitle,
} from './styles';
import AssignPlaylistButton from 'pages/Playlists/PlaylistDetail/AssignPlaylistButton';
import BulkActionBar from '../BulkActionBar';
import { IUsersAssignedParams } from './types';
import UsersTable from '../UsersTable';

type EmptyStateProps = Pick<
  IUsersAssignedParams,
  'isSonar' | 'playlist' | 'setFetching' | 'onRefreshAfterAssignment'
>;

const EmptyState = ({
  isSonar,
  playlist,
  setFetching,
  onRefreshAfterAssignment,
}: EmptyStateProps): JSX.Element => {
  const username = `${playlist?.created_for?.first_name} ${playlist?.created_for?.last_name}`;
  return isSonar ? (
    <EmptySonarStateContainer>
      <SonarEmptyStateTitle>Ready to assign playlist?</SonarEmptyStateTitle>
      <SonarEmptyStateSubtitle>
        This playlist was created for <strong>{username}</strong>, but it hasn’t been assigned yet.
        Assign it now to send tailored training recommendations.
      </SonarEmptyStateSubtitle>
      <AssignPlaylistButton
        playlist={playlist}
        setFetching={setFetching}
        onRefreshAfterAssignment={onRefreshAfterAssignment}
      />
    </EmptySonarStateContainer>
  ) : (
    <NoUsersTitle>You haven’t added any users yet</NoUsersTitle>
  );
};

const UsersAssigned = ({
  playlistUsers,
  fetchingUsers,
  callRemoveUser,
  playlist,
  isSonar,
  isAccountPlaylist,
  setFetching,
  onRefreshAfterAssignment,
  disableManualAssignments,
  pagination,
  onUsersPaginationChange,
  deadlineBulkActionsEnabled,
  selectedDeadlineFilters,
  sortState,
  hasAssignedUsers = false,
  selectedUserIds = [],
  allUsersSelected,
  hasPartiallySelectedUsers,
  onToggleSelectAllUsers,
  onToggleUserSelection,
  onClearSelection,
  onEditDeadlines,
  onEditDeadline,
  onDeadlineFiltersChange,
  onSortToggle,
  onSortSelect,
}: IUsersAssignedParams): JSX.Element => {
  const selectedCount = selectedUserIds.length;
  const hasSelectedDeadlineFilters = Boolean(selectedDeadlineFilters?.length);
  const hasBulkActionBar = Boolean(
    deadlineBulkActionsEnabled && selectedCount > 0 && onClearSelection && onEditDeadlines
  );
  const shouldRenderTable =
    fetchingUsers || playlistUsers.length > 0 || (hasAssignedUsers && hasSelectedDeadlineFilters);

  return (
    <Container>
      {shouldRenderTable ? (
        <>
          {hasBulkActionBar && (
            <BulkActionBar
              selectedCount={selectedCount}
              isAllAssignedSelected={Boolean(allUsersSelected)}
              onClearSelection={onClearSelection}
              onEditDeadline={onEditDeadlines}
            />
          )}
          <UsersTable
            fetching={fetchingUsers}
            lock={fetchingUsers && playlistUsers.length > 0}
            playlistUsers={playlistUsers}
            playlist={playlist}
            removeUser={callRemoveUser}
            isSonar={isSonar}
            isAccountPlaylist={isAccountPlaylist}
            disableManualAssignments={disableManualAssignments}
            pagination={pagination}
            onPaginationChange={onUsersPaginationChange}
            selectionEnabled={deadlineBulkActionsEnabled}
            selectedDeadlineFilters={selectedDeadlineFilters}
            sortState={sortState}
            selectedUserIds={selectedUserIds}
            allUsersSelected={allUsersSelected}
            hasPartiallySelectedUsers={hasPartiallySelectedUsers}
            onToggleSelectAllUsers={onToggleSelectAllUsers}
            onToggleUserSelection={onToggleUserSelection}
            hasBulkActionBar={hasBulkActionBar}
            onEditDeadline={onEditDeadline}
            onDeadlineFiltersChange={onDeadlineFiltersChange}
            onSortToggle={onSortToggle}
            onSortSelect={onSortSelect}
          />
        </>
      ) : (
        <EmptyState
          isSonar={isSonar}
          playlist={playlist}
          setFetching={setFetching}
          onRefreshAfterAssignment={onRefreshAfterAssignment}
        />
      )}
    </Container>
  );
};

export default UsersAssigned;
